import { Avatar, AvatarFallback, getAvatarColor } from '@heidi/design-system/avatar';
import { focusRing } from '@heidi/design-system/styles';
import { typographyVariant } from '@heidi/design-system/typography';
import { cn, getInitials } from '@heidi/utils';
import { Blocks, ChevronRight, FileText, Unlink2 } from 'lucide-react';
import type { ReactNode } from 'react';
import { defineMessages, useIntl } from 'react-intl';

import { clampObjectGist } from '../../../../data/projects/timeline/object-card/objectListFormat';
import { objectReferenceMetadataMessages } from '../../../composer/object-reference-metadata.messages';

const messages = defineMessages({
  openPatient: { defaultMessage: 'Open patient {name}', id: '44kOqzKnQG' },
  openSession: { defaultMessage: 'Open session {title}', id: 'M+xluFP+sy' },
  openDocument: { defaultMessage: 'Open document {title}', id: '8cpQzEY/1D' },
  openTemplate: { defaultMessage: 'Open template {title}', id: 'ikULsxtHg9' },
  openRoutine: { defaultMessage: 'Open routine {title}', id: 'Y3qekNMdPu' },
  openTask: { defaultMessage: 'Open task details for {title}', id: '/exPofKJUl' },
});

const text = typographyVariant({ variant: 'p2' });

const rowClass = cn(
  'flex min-h-10 min-w-0 items-center gap-2 rounded-md px-3 py-2 text-start transition-colors',
  'hover:bg-surface-hover',
  focusRing
);

const ObjectListRow = ({
  onOpen,
  openLabel,
  children,
}: {
  onOpen?: () => void;
  openLabel?: string;
  children: ReactNode;
}): JSX.Element =>
  onOpen ? (
    <li data-testid="object-list-row">
      <button
        type="button"
        onClick={onOpen}
        aria-label={openLabel}
        className={cn(rowClass, 'w-full cursor-pointer')}
      >
        {children}
      </button>
    </li>
  ) : (
    <li data-testid="object-list-row" className={cn(rowClass, 'cursor-default')}>
      {children}
    </li>
  );

const PatientMark = ({
  name,
  avatarSeed,
  className,
}: {
  name?: string;
  avatarSeed?: string;
  className?: string;
}): JSX.Element => {
  const intl = useIntl();
  if (!name) {
    return (
      <Avatar
        size="xs"
        color="empty"
        className={cn('shrink-0', className)}
        aria-label={intl.formatMessage(objectReferenceMetadataMessages.noPatientLinked)}
      >
        <AvatarFallback icon={<Unlink2 aria-hidden />} />
      </Avatar>
    );
  }
  return (
    <Avatar
      size="xs"
      color={getAvatarColor(avatarSeed ?? name)}
      className={cn('shrink-0', className)}
    >
      <AvatarFallback>{getInitials(name)}</AvatarFallback>
    </Avatar>
  );
};

export const PendingObjectRow = ({ title }: { title: string }): JSX.Element => (
  <ObjectListRow>
    <span className={cn(text, 'min-w-0 truncate text-text-tertiary')}>{title}</span>
  </ObjectListRow>
);

export const PatientObjectRow = ({
  name,
  avatarSeed,
  demographics,
  lastSeen,
  onOpen,
}: {
  name: string;
  avatarSeed?: string;
  demographics?: string;
  lastSeen?: string;
  onOpen?: () => void;
}): JSX.Element => {
  const intl = useIntl();
  return (
    <ObjectListRow
      onOpen={onOpen}
      openLabel={intl.formatMessage(messages.openPatient, { name })}
    >
      <PatientMark name={name} avatarSeed={avatarSeed} />
      <span className="flex min-w-0 flex-1 items-center gap-2">
        <span className={cn(text, 'shrink-0 text-text-primary')}>{name}</span>
        {demographics ? (
          <span className={cn(text, 'min-w-0 truncate text-text-tertiary')}>
            {demographics}
          </span>
        ) : null}
      </span>
      {lastSeen ? (
        <span className={cn(text, 'shrink-0 whitespace-nowrap text-text-tertiary')}>
          {lastSeen}
        </span>
      ) : null}
    </ObjectListRow>
  );
};

export const SessionObjectRow = ({
  title,
  when,
  gist,
  patientName,
  avatarSeed,
  onOpen,
}: {
  title: string;
  when: string;
  gist?: string;
  patientName?: string;
  avatarSeed?: string;
  onOpen?: () => void;
}): JSX.Element => {
  const intl = useIntl();
  const summary = clampObjectGist(gist);
  return (
    <ObjectListRow
      onOpen={onOpen}
      openLabel={intl.formatMessage(messages.openSession, { title })}
    >
      <span className={cn(text, 'shrink-0 text-text-primary')}>{when}</span>
      {summary ? (
        <span className={cn(text, 'min-w-0 truncate text-text-tertiary')}>{summary}</span>
      ) : null}
      <PatientMark name={patientName} avatarSeed={avatarSeed} className="ms-auto" />
    </ObjectListRow>
  );
};

export const TaskObjectRow = ({
  title,
  patientName,
  statusSlot,
  assigneeSlot,
  onOpen,
}: {
  title: string;
  patientName?: string;
  statusSlot: ReactNode;
  assigneeSlot: ReactNode;
  onOpen?: () => void;
}): JSX.Element => {
  const intl = useIntl();
  const label = (
    <>
      {patientName ? (
        <>
          <span className={cn(text, 'shrink-0 truncate text-text-tertiary')}>
            {patientName}
          </span>
          <ChevronRight
            className="size-3 shrink-0 text-text-tertiary rtl:-scale-x-100"
            aria-hidden
          />
        </>
      ) : null}
      <span className={cn(text, 'truncate text-text-primary')}>{title}</span>
    </>
  );
  return (
    <ObjectListRow>
      {statusSlot}
      {onOpen ? (
        <button
          type="button"
          onClick={onOpen}
          aria-label={intl.formatMessage(messages.openTask, { title })}
          className={cn(
            'flex min-w-0 flex-1 cursor-pointer items-center gap-1 text-start focus-visible:rounded-sm',
            focusRing
          )}
        >
          {label}
        </button>
      ) : (
        <span className="flex min-w-0 flex-1 items-center gap-1">{label}</span>
      )}
      {assigneeSlot}
    </ObjectListRow>
  );
};

export const DocumentObjectRow = ({
  title,
  when,
  gist,
  patientName,
  avatarSeed,
  onOpen,
}: {
  title: string;
  when?: string;
  gist?: string;
  patientName?: string;
  avatarSeed?: string;
  onOpen?: () => void;
}): JSX.Element => {
  const intl = useIntl();
  const summary = clampObjectGist(gist);
  return (
    <ObjectListRow
      onOpen={onOpen}
      openLabel={intl.formatMessage(messages.openDocument, { title })}
    >
      <FileText aria-hidden className="size-4 shrink-0 text-text-secondary" />
      <span className={cn(text, 'min-w-0 flex-1 truncate text-text-primary')}>
        {title}
      </span>
      {when ? (
        <span className={cn(text, 'shrink-0 text-text-primary')}>{when}</span>
      ) : null}
      {summary ? (
        <span className={cn(text, 'min-w-0 max-w-40 truncate text-text-tertiary')}>
          {summary}
        </span>
      ) : null}
      {when ? <PatientMark name={patientName} avatarSeed={avatarSeed} /> : null}
    </ObjectListRow>
  );
};

export const TemplateObjectRow = ({
  title,
  editedOn,
  onOpen,
}: {
  title: string;
  editedOn?: string;
  onOpen?: () => void;
}): JSX.Element => {
  const intl = useIntl();
  return (
    <ObjectListRow
      onOpen={onOpen}
      openLabel={intl.formatMessage(messages.openTemplate, { title })}
    >
      <Blocks aria-hidden className="size-4 shrink-0 text-text-secondary" />
      <span className={cn(text, 'min-w-0 flex-1 truncate text-text-primary')}>
        {title}
      </span>
      {editedOn ? (
        <span className={cn(text, 'shrink-0 text-text-tertiary')}>
          {intl.formatMessage(objectReferenceMetadataMessages.lastEdited, {
            date: editedOn,
          })}
        </span>
      ) : null}
    </ObjectListRow>
  );
};

export const RoutineObjectRow = ({
  title,
  description,
  onOpen,
}: {
  title: string;
  description?: string;
  onOpen?: () => void;
}): JSX.Element => {
  const intl = useIntl();
  return (
    <ObjectListRow
      onOpen={onOpen}
      openLabel={intl.formatMessage(messages.openRoutine, { title })}
    >
      <span className={cn(text, 'min-w-0 truncate text-text-primary')}>{title}</span>
      {description ? (
        <span className={cn(text, 'min-w-0 flex-1 truncate text-text-tertiary')}>
          {description}
        </span>
      ) : null}
    </ObjectListRow>
  );
};
